import type { PaymentCallbackTypeInput } from '../types/paymentCallback.type';
import { PAYMENT_CALLBACK_DISCRIMINATOR } from '../constants/paymentCallback.constant';
import { firstRouteValue } from './routeValue.util';

/**
 * Extrae el discriminador de vista desde el parámetro `type` con el que una pasarela de pago
 * retorna a la web, descartando el query string que la pasarela haya concatenado con `?`
 * en lugar de `&`.
 * @param rawType - Valor crudo de `route.query.type`; puede ser texto, arreglo, null o undefined.
 * @returns El discriminador limpio, o cadena vacía si no hay un valor utilizable.
 * @example
 * sanitizeCallbackType('inswitch-success?gateway=kushkicard&paymentId=21722') // 'inswitch-success'
 * sanitizeCallbackType('wompi-success') // 'wompi-success'
 * sanitizeCallbackType(undefined) // ''
 */
export function sanitizeCallbackType(rawType: unknown): string {
  if (rawType === null || rawType === undefined) return '';
  if (Array.isArray(rawType)) return sanitizeCallbackType(rawType[0]);

  const [discriminator] = String(rawType).split('?');
  return discriminator.trim();
}

/**
 * Resuelve el discriminador de vista para el retorno de una pasarela de pago,
 * priorizando el `type` explícito, luego `token`, luego `cashin` (con o sin
 * referencia Wompi) y, por último, una referencia InSwitch en almacenamiento.
 * @param input - Query de la ruta y booleanos de referencia de Wompi/InSwitch.
 * @returns El discriminador resuelto, o cadena vacía si ninguna regla aplica.
 */
export function resolvePaymentCallbackType(input: PaymentCallbackTypeInput): string {
  const { query, hasWompiReference, hasInswitchReference } = input;

  const explicitType = sanitizeCallbackType(query.type);
  if (explicitType) return explicitType;

  if (query.token) return PAYMENT_CALLBACK_DISCRIMINATOR.VOUCHER;

  if (query.cashin && hasWompiReference) return PAYMENT_CALLBACK_DISCRIMINATOR.WOMPI_SUCCESS;

  if (query.cashin) return firstRouteValue(query.cashin);

  if (hasInswitchReference) return PAYMENT_CALLBACK_DISCRIMINATOR.INSWITCH_SUCCESS;

  return '';
}
